import {Component, computed, effect, inject, input, output, signal, untracked, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {FuseConfigService} from '../../services/config.service';
import {MaterialModule} from '../../modules/material.module';
import {SharedModule} from '../../modules/shared.module';
import {MatSelectionList} from '@angular/material/list';

interface FilterItem { id: number | string; name?: string; selected: boolean; }
interface FiltersVM {
  countries?: FilterItem[];
  cantons?: FilterItem[];
  organizations?: FilterItem[];
  cantonFB?: FilterItem[];
  organizationsFB?: FilterItem[];
  targetGroups?: FilterItem[];
  eventTypes?: FilterItem[];
  roles?: FilterItem[];
  tags?: FilterItem[];
  platforms?: { id: number | string; title: string; selected?: boolean }[];
  blogTypes?: { id: number | string; name: string; selected?: boolean }[];
  marketplaceCategories?: any[];
  marketplaceType?: boolean;
  marketplaceStatus?: boolean;
  blobVisibility?: boolean;
  learningPathsVisibility?: boolean;
  // flags
  eventRegistration?: boolean;
  mileStoneEvent?: boolean;
  migrate?: boolean;
  publicEvents?: 'internal' | 'public' | '';
  myEvents?: 'mine' | 'all' | '';
  activated?: boolean;
  recommendator?: boolean;
  noActivated?: boolean;
}

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [MaterialModule, SharedModule]
})


export class FilterComponent {
  eventTypeList = viewChild<MatSelectionList>('eventTypeList');
  targetGroupsList = viewChild<MatSelectionList>('targetGroupsList');

  filters = input.required<FiltersVM>();
  page = input.required<'admin' | 'event' | 'budget' | 'organization' | 'user' | 'calendar' | 'canton' | 'marketplace'>();
  filterFromDB = input.required<any>();
  chooseFilter = output<{ choose: any }>();

  // private route = inject(ActivatedRoute);
  private fuseConfig = inject(FuseConfigService);

  // queryParams = toSignal(this.route.queryParams, { initialValue: {} as any });
  activeFilters = this.fuseConfig.activeFilter_Sig;

  // countryIdRoute = computed(() => (this.queryParams() as any)?.country ?? null);
  // cityIdRoute = computed(() => (this.queryParams() as any)?.subCountry ?? null);
  // orgIdRoute = computed(() => (this.queryParams() as any)?.orgId ?? null);

  showCountry = signal(true);
  showCanton = signal(true);
  showOrganization = signal(true);
  showTag = signal(true);
  showRole = signal(true);
  showTargetGroup = signal(false);
  showEventType = signal(false);

  showCantonFB = signal(true);
  showOrganizationFB = signal(true);
  showMarketplaceCategories = signal(true);

  // UI models for radio groups/clean actions
  platformsModel = signal<any>(null);
  countryModel = signal<any>(null);
  eventRegistrationModel = signal<any>(null);
  mileStoneEventModel = signal<any>(null);
  myEventsModel = signal<any>(null);
  publicEventsModel = signal<any>(null);
  eventTypeModel = signal<any[]>([]);
  targetGroupsModel = signal<any[]>([]);

  publicBlogsModel = signal<string>('');
  learningPathsVisibilityModel = signal<string>('');
  marketplaceType = signal<string>('');
  marketplaceStatus = signal<string>('');

  private pageKey = computed(() => this.page());
  private dbForPage = computed(() => {
    const db = this.filterFromDB();
    if (!db) { return {} as any; }
    return db;
  });

  private selectedSet = (prop: string) => computed(() => {
    const val = this.dbForPage()?.[prop];
    if (val == null) { return new Set<string | number>(); }
    return new Set(Array.isArray(val) ? val.map(Number) : [Number(val)]);
  })

  selectedCountries = this.selectedSet('country');
  selectedCountrySingle = computed(() => {
    const v = this.dbForPage()?.['country'];
    return v == null ? null : Number(v);
  });
  selectedCantons = this.selectedSet('cantons');
  selectedOrganizations = this.selectedSet('organizations');
  selectedEventTypes = this.selectedSet('eventTypes');
  selectedTargetGroups = this.selectedSet('targetGroups');
  selectedTags = this.selectedSet('tags');
  selectedRoles = this.selectedSet('roles');

  vm = computed(() => {
    const base = this.filters();
    const page = this.pageKey();
    const db = this.dbForPage();

    const mark = <T extends FilterItem>(items: T[] | undefined, set: Set<number | string>, single?: number | string) =>
      (items ?? []).map(it => ({
        ...it,
        selected: single != null ? Number(it.id) === Number(single) : set.has(Number(it.id))
      }));

    return {
      ...base,
      countries: mark(base.countries, this.selectedCountries(), this.selectedCountrySingle()),
      cantons: mark(base.cantons, this.selectedCantons()),
      organizations: mark(base.organizations, this.selectedOrganizations()),
      eventTypes: mark(base.eventTypes, this.selectedEventTypes()),
      targetGroups: mark(base.targetGroups, this.selectedTargetGroups()),
      roles: mark(base.roles, this.selectedRoles()),
      tags: mark(base.tags, this.selectedTags()),
      eventRegistration: db?.eventRegistration ?? base.eventRegistration,
      mileStoneEvent: db?.mileStoneEvent ?? base.mileStoneEvent,
      migrate: db?.migrate ?? base.migrate,
      publicEvents: db?.publicEvents ?? base.publicEvents,
      myEvents: db?.myEvents ?? base.myEvents,
      activated: (this.filterFromDB()?.admin?.activated) ?? base.activated,
      recommendator: (this.filterFromDB()?.admin?.recommendator) ?? base.recommendator,
      noActivated: (this.filterFromDB()?.admin?.noActivated) ?? base.noActivated,
    } as FiltersVM & { [k: string]: any };
  });

  choose = (e: any) => {
    if (e === 'all') {
      this.platformsModel.set(null);
      this.eventRegistrationModel.set(false);
      this.mileStoneEventModel.set(false);
      this.myEventsModel.set(false);
      this.publicEventsModel.set(null);
      this.eventTypeModel.set([]);
      this.eventTypeList()?.deselectAll();
      this.countryModel.set(null);
      this.targetGroupsModel.set([]);
      this.targetGroupsList()?.deselectAll();

      this.marketplaceType.set('');
      this.marketplaceStatus.set('');
    }
    this.chooseFilter.emit({ choose: e });
  }
  chooseCanton = (e: any) => this.chooseFilter.emit({ choose: { canton: e.options[0].value, selected: e.options[0].selected } });
  chooseRole = (e: any) => this.chooseFilter.emit({ choose: { role: e.options[0].value, selected: e.options[0].selected } });
  chooseTG = (e: any) => {
    this.chooseFilter.emit({ choose: { targetGroups: e.options[0].value, selected: e.options[0].selected } });
    const selectedIds = e.source.selectedOptions.selected.map(option => option.value);
    this.targetGroupsModel.set(selectedIds);
  }
  chooseMarketplaceCategory = (e: any) => this.chooseFilter.emit({ choose: { marketplaceCategory: e.options[0].value, selected: e.options[0].selected } });
  chooseOrg = (e: any) => this.chooseFilter.emit({ choose: { organization: e.options[0].value, selected: e.options[0].selected } });
  chooseEventType = (e: any) => {
    this.chooseFilter.emit({ choose: { eventType: e.options[0].value, selected: e.options[0].selected } });
    const selectedIds = e.source.selectedOptions.selected.map(option => option.value);
    this.eventTypeModel.set(selectedIds);
  }
  chooseTags = (e: any) => this.chooseFilter.emit({ choose: { tag: e.options[0].value, selected: e.options[0].selected } });

  constructor() {
    effect(() => {
      const dbData = this.dbForPage();

      untracked(() => {
        if (dbData && Object.keys(dbData).length > 0) {
          if (dbData.country) {
            const countryId = Number(dbData.country);
            this.choose({ country: countryId });
          }

          if (dbData.cantons) {
            this.choose({ canton: dbData.cantons });
          }

          if (dbData.organizations) {
            this.choose({ organization: dbData.organizations });
          }

          if (dbData.targetGroups?.length) {
            this.showTargetGroup.set(true);
            this.targetGroupsModel.set(dbData.targetGroups);
            this.choose({ targetGroups: dbData.targetGroups });
          }

          if (dbData.eventTypes?.length) {
            this.showEventType.set(true);
            this.eventTypeModel.set(dbData.eventTypes);
            this.choose({ eventType: dbData.eventTypes });
          }

          if (dbData.myEvents) {
            this.myEventsModel.set(dbData.myEvents);
            this.choose({myEvents: true, selected: dbData.myEvents});
          }

          if (dbData.eventRegistration) {
            this.eventRegistrationModel.set(dbData.eventRegistration);
            this.choose({eventRegistration: true, selected: dbData.eventRegistration});
          }

          if (dbData.mileStoneEvent) {
            this.mileStoneEventModel.set(dbData.mileStoneEvent);
            this.choose({mileStoneEvent: true, selected: dbData.mileStoneEvent});
          }

          if (dbData.publicEvents) {
            this.publicEventsModel.set(dbData.publicEvents);
            this.choose({publicEvents: dbData.publicEvents});
          }

          if (dbData.platforms) {
            this.platformsModel.set(+dbData.platforms);
            this.choose({platforms: +dbData.platforms});
          }
        }
      });
    });
  }
}
